import {createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState,} from 'react';

import {PaymentProvider, PlanInterval, User} from '@/lib/api';

interface ProviderProps {
  children: ReactNode;
}

export interface UserContext {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
}

const UserContext = createContext<UserContext | null>(null);

export const UserProvider = ({ children }: ProviderProps) => {
  const [user, setUser] = useState<User | null>(() => {
    const localUser = localStorage.getItem('user');

    if (localUser) {
      return JSON.parse(localUser);
    }

    return null;
  });

  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }
  }, [user]);

  const value = useMemo(() => {
    const paymentProvider = user?.payment_provider || PaymentProvider.STRIPE;
    const isStripeUser = paymentProvider === PaymentProvider.STRIPE;
    const isPaddleUser = paymentProvider === PaymentProvider.PADDLE;
    const hasYearlyPlan = user?.plan?.interval === PlanInterval.YEAR;
    const hasMonthlyPlan = user?.plan?.interval === PlanInterval.MONTH;
    const hasPlan = Boolean(user?.plan);

    return {
      user,
      setUser,
      paymentProvider,
      isStripeUser,
      isPaddleUser,
      hasYearlyPlan,
      hasMonthlyPlan,
      hasPlan,
    };
  }, [user]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return context;
};
